import { useCallback, useEffect, useRef, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";

import {
  inferCarrierFromIccid,
  isLikelyVerizonIccid,
} from "@integrator-src/manufacturing/sim-iccid-carrier";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubTouchSelect } from "@/components/ui/HubTouchSelect";
import { Input, Label } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

import { SimActivateCarrierModal } from "./SimActivateCarrierModal";
import {
  activateSimWithCarrierApi,
  bulkUpsertSimCards,
  saveSimCard,
  fetchSimCards,
  fetchSimLookup,
  fetchSimCarrierApiStatus,
  fetchSimCarriers,
  SIM_CARDS_QUERY_KEY,
  type SimCardRow,
  type SimCsvErrorRow,
  type SimCsvValidRow,
  validateSimCsv,
} from "./simActivationApi";

const PAGE_HELP =
  "Look up ICCIDs in hub SIM inventory (carrier, activated status, customer owned, notes). Add a customer-owned card from this page, or upload a CSV. Carrier API activation is only for Verizon, Vodafone, and AT&T stock.";

const PAGE_SIZE = 50;
const LOOKUP_DEBOUNCE_MS = 300;
const EXACT_LOOKUP_MIN_LENGTH = 18;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function normalizeLookupIccid(raw: string): string {
  return raw.trim().toLowerCase();
}

function carrierSupportsApi(carrier: string): boolean {
  const normalized = carrier.trim().toLowerCase();
  return normalized === "verizon" || normalized === "vodafone" || normalized === "at&t";
}

function FilterChipGroup<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}): JSX.Element {
  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-slate-600">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <Button
            key={option.value || "__all__"}
            type="button"
            size="sm"
            variant={value === option.value ? "primary" : "secondary"}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function SimActivationPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const writesAllowed = useHubModuleWrite("manufacturing");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [lookupIccid, setLookupIccid] = useState("");
  const [carrierFilter, setCarrierFilter] = useState("");
  const [activatedFilter, setActivatedFilter] = useState<"" | "yes" | "no">("");
  const [offset, setOffset] = useState(0);
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateModalIccid, setActivateModalIccid] = useState("");
  const [activateModalCarrier, setActivateModalCarrier] = useState("Verizon");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadCustomerOwned, setUploadCustomerOwned] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addCarrier, setAddCarrier] = useState("");
  const [addCustomerOwned, setAddCustomerOwned] = useState(true);
  const [addActivated, setAddActivated] = useState(true);
  const [addIccid, setAddIccid] = useState("");
  const addIccidRef = useRef<HTMLInputElement>(null);
  const [uploadValid, setUploadValid] = useState<SimCsvValidRow[]>([]);
  const [uploadErrors, setUploadErrors] = useState<SimCsvErrorRow[]>([]);
  const [uploadTab, setUploadTab] = useState<"valid" | "errors">("valid");
  const [activateWithCarrier, setActivateWithCarrier] = useState(true);

  const debouncedLookupIccid = useDebouncedValue(lookupIccid, LOOKUP_DEBOUNCE_MS);

  useEffect(() => {
    setOffset(0);
  }, [debouncedLookupIccid, carrierFilter, activatedFilter]);

  const listQuery = useQuery({
    queryKey: [
      ...SIM_CARDS_QUERY_KEY,
      "page",
      debouncedLookupIccid,
      carrierFilter,
      activatedFilter,
      offset,
    ],
    queryFn: () =>
      fetchSimCards({
        limit: PAGE_SIZE,
        offset,
        iccid: debouncedLookupIccid,
        carrier: carrierFilter,
        activated: activatedFilter,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  const exactLookupQuery = useQuery({
    queryKey: [...SIM_CARDS_QUERY_KEY, "lookup", debouncedLookupIccid],
    queryFn: () => fetchSimLookup(debouncedLookupIccid),
    enabled: normalizeLookupIccid(debouncedLookupIccid).length >= EXACT_LOOKUP_MIN_LENGTH,
  });

  const carriersQuery = useQuery({
    queryKey: [...SIM_CARDS_QUERY_KEY, "carriers"],
    queryFn: fetchSimCarriers,
  });

  const carrierApiQuery = useQuery({
    queryKey: [...SIM_CARDS_QUERY_KEY, "carrier-api-status"],
    queryFn: fetchSimCarrierApiStatus,
  });

  const lookupNeedle = normalizeLookupIccid(lookupIccid);
  const debouncedLookupNeedle = normalizeLookupIccid(debouncedLookupIccid);
  const lookupComplete = debouncedLookupNeedle.length >= EXACT_LOOKUP_MIN_LENGTH;
  const tableRows = listQuery.data?.sims ?? [];
  const totalCount = listQuery.data?.total ?? 0;
  const exactLookupMatch =
    lookupComplete && !exactLookupQuery.isLoading ? exactLookupQuery.data ?? null : null;
  const inferredCarrier = lookupComplete ? inferCarrierFromIccid(debouncedLookupNeedle) : null;
  const likelyVerizon = lookupComplete ? isLikelyVerizonIccid(debouncedLookupNeedle) : false;

  const displayRows: SimCardRow[] =
    exactLookupMatch && !tableRows.some((row) => row.iccid === exactLookupMatch.iccid)
      ? [exactLookupMatch]
      : tableRows;

  const showMissingLookupRow =
    lookupComplete && !exactLookupQuery.isLoading && exactLookupMatch == null && lookupNeedle !== "";

  const pageStart = totalCount === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + tableRows.length, totalCount);
  const hasPreviousPage = offset > 0;
  const hasNextPage = offset + PAGE_SIZE < totalCount;

  const openActivateModal = useCallback((iccid: string, carrier: string) => {
    setActivateModalIccid(iccid.trim());
    setActivateModalCarrier(carrier);
    setActivateModalOpen(true);
  }, []);

  const invalidateSims = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: SIM_CARDS_QUERY_KEY });
  }, [queryClient]);

  const validateCsvMutation = useMutation({
    mutationFn: async (file: File) => {
      const csvText = await file.text();
      return validateSimCsv(csvText);
    },
    onSuccess: (result) => {
      setUploadValid(result.valid);
      setUploadErrors(result.errors);
      setUploadTab(result.valid.length > 0 ? "valid" : "errors");
      setUploadOpen(true);
      if (result.errors.length > 0) {
        showToast({
          tone: "info",
          message: `${result.errors.length} row(s) moved to the errors table.`,
        });
      }
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const bulkUpsertMutation = useMutation({
    mutationFn: (activated: boolean) =>
      bulkUpsertSimCards({
        records: uploadValid.map(({ iccid, carrier }) => ({ iccid, carrier })),
        activated,
        customerOwned: uploadCustomerOwned ? true : undefined,
        activateWithCarrier: activated && activateWithCarrier && !uploadCustomerOwned,
      }),
    onSuccess: (data, activated) => {
      showToast({
        tone: "success",
        message: `Updated ${data.upserted} SIM(s)${activated ? " as activated" : " as inactive"}${uploadCustomerOwned ? ", customer owned" : ""}.`,
      });
      setUploadOpen(false);
      setUploadValid([]);
      setUploadErrors([]);
      void queryClient.invalidateQueries({ queryKey: SIM_CARDS_QUERY_KEY });
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const saveSimMutation = useMutation({
    mutationFn: () =>
      saveSimCard({
        iccid: addIccid,
        carrier: addCarrier,
        activated: addActivated,
        customerOwned: addCustomerOwned,
      }),
    onSuccess: () => {
      showToast({ tone: "success", message: `Saved ${addIccid.trim()}.` });
      setAddIccid("");
      void queryClient.invalidateQueries({ queryKey: SIM_CARDS_QUERY_KEY });
      requestAnimationFrame(() => addIccidRef.current?.focus());
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
      addIccidRef.current?.focus();
    },
  });

  useEffect(() => {
    if (!addOpen) return;
    addIccidRef.current?.focus();
  }, [addOpen]);

  const singleActivateMutation = useMutation({
    mutationFn: ({ iccid, carrier }: { iccid: string; carrier: string }) =>
      activateSimWithCarrierApi({
        iccid: iccid.trim(),
        carrier,
      }),
    onMutate: ({ carrier }) => {
      showToast({
        tone: "info",
        message: `Submitting ${carrier} activation — this may take a moment…`,
      });
    },
    onSuccess: async (data) => {
      showToast({ tone: "success", message: data.activation.message });
      setActivateModalOpen(false);
      setActivateModalIccid("");
      setLookupIccid("");
      await invalidateSims();
    },
    onError: (error: Error) => {
      showToast({ tone: "error", message: error.message });
    },
  });

  const carrierOptions = carriersQuery.data ?? [];
  const carrierApis = carrierApiQuery.data?.carrierApis;
  const apiReadyForCarrier = useCallback(
    (carrier: string | null): boolean => {
      if (!carrierApis || !carrier) {
        return false;
      }
      const normalized = carrier.trim().toLowerCase();
      if (normalized === "verizon") {
        return carrierApis.verizon;
      }
      if (normalized === "vodafone") {
        return carrierApis.vodafone;
      }
      if (normalized === "at&t") {
        return carrierApis.att;
      }
      return false;
    },
    [carrierApis],
  );

  const uploadHasApiEligibleRows =
    activateWithCarrier &&
    uploadValid.some((row) => carrierSupportsApi(row.carrier));

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    validateCsvMutation.mutate(file);
  };

  const missingLookupMessage = (() => {
    if (!showMissingLookupRow) {
      return null;
    }
    if (inferredCarrier && carrierSupportsApi(inferredCarrier)) {
      return `No exact match for this ICCID in inventory. Prefix indicates ${inferredCarrier}; carrier API activation is available when credentials are configured.`;
    }
    if (likelyVerizon) {
      return "No exact match for this ICCID in inventory. Prefix indicates Verizon.";
    }
    if (inferredCarrier) {
      return `No exact match for this ICCID in inventory. Prefix indicates ${inferredCarrier}; carrier API activation is not available for this card.`;
    }
    return "No exact match for this ICCID in inventory. Carrier could not be inferred from the ICCID prefix.";
  })();

  const missingLookupActivateCarrier =
    inferredCarrier && carrierSupportsApi(inferredCarrier) ? inferredCarrier : likelyVerizon ? "Verizon" : null;

  return (
    <div className="space-y-4 pb-8">
      <PageTitleWithHelp hoverHint={PAGE_HELP}>SIM</PageTitleWithHelp>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>Inventory</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFileChange}
            />
            <ReadOnlyHoverLayer active={!writesAllowed} hoverHint="Manufacturing write access is required.">
              <Button
                variant="primary"
                size="sm"
                disabled={!writesAllowed}
                onClick={() => setAddOpen(true)}
              >
                Add SIM
              </Button>
            </ReadOnlyHoverLayer>
            <ReadOnlyHoverLayer active={!writesAllowed} hoverHint="Manufacturing write access is required.">
              <Button
                variant="secondary"
                size="sm"
                disabled={!writesAllowed || validateCsvMutation.isPending}
                onClick={() => fileInputRef.current?.click()}
              >
                {validateCsvMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-4 w-4" />
                )}
                Upload CSV
              </Button>
            </ReadOnlyHoverLayer>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <Label htmlFor="sim-lookup-iccid">ICCID lookup</Label>
            <Input
              id="sim-lookup-iccid"
              type="search"
              placeholder="Paste or type an ICCID…"
              value={lookupIccid}
              onChange={(event) => setLookupIccid(event.target.value)}
              className="mt-1.5 font-mono"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <FilterChipGroup
              label="Carrier"
              value={carrierFilter}
              options={[
                { value: "", label: "All" },
                ...carrierOptions.map((carrier) => ({ value: carrier, label: carrier })),
              ]}
              onChange={setCarrierFilter}
            />
            <FilterChipGroup
              label="Activated"
              value={activatedFilter}
              options={[
                { value: "", label: "All" },
                { value: "yes", label: "Activated" },
                { value: "no", label: "Not activated" },
              ]}
              onChange={setActivatedFilter}
            />
          </div>

          {listQuery.isLoading && !listQuery.data ? (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading SIM inventory…
            </div>
          ) : listQuery.isError ? (
            <p className="text-sm text-rose-700">{listQuery.error.message}</p>
          ) : (
            <>
              {!showMissingLookupRow ? (
                <p className="text-sm text-slate-600">
                  {totalCount === 0
                    ? "No SIMs match this lookup and filters."
                    : `Showing ${pageStart.toLocaleString()}–${pageEnd.toLocaleString()} of ${totalCount.toLocaleString()} SIM(s)`}
                </p>
              ) : null}
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="px-3 py-2">Created</th>
                      <th className="px-3 py-2">ICCID</th>
                      <th className="px-3 py-2">Carrier</th>
                      <th className="px-3 py-2">Activated</th>
                      <th className="px-3 py-2">Customer owned</th>
                      <th className="px-3 py-2">Note</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {showMissingLookupRow ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-6">
                          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-sm text-amber-800">{missingLookupMessage}</p>
                            {missingLookupActivateCarrier && writesAllowed ? (
                              <ReadOnlyHoverLayer
                                active={!apiReadyForCarrier(missingLookupActivateCarrier)}
                                hoverHint={`${missingLookupActivateCarrier} API credentials are not configured on this server.`}
                              >
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="primary"
                                  disabled={!apiReadyForCarrier(missingLookupActivateCarrier)}
                                  onClick={() =>
                                    openActivateModal(debouncedLookupNeedle, missingLookupActivateCarrier)
                                  }
                                >
                                  Activate with {missingLookupActivateCarrier}
                                </Button>
                              </ReadOnlyHoverLayer>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : displayRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                          No SIMs on this page.
                        </td>
                      </tr>
                    ) : (
                      displayRows.map((row) => (
                        <tr
                          key={row.iccid}
                          className={
                            exactLookupMatch?.iccid === row.iccid ? "bg-sky-50/80" : undefined
                          }
                        >
                          <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                            {new Date(row.created_at).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{row.iccid}</td>
                          <td className="px-3 py-2">{row.carrier}</td>
                          <td className="px-3 py-2">
                            {row.activated ? (
                              <Badge tone="success">Yes</Badge>
                            ) : (
                              <Badge tone="neutral">No</Badge>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {row.customer_owned ? (
                              <Badge tone="neutral">Yes</Badge>
                            ) : (
                              <Badge tone="neutral">No</Badge>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-600">{row.note ?? "—"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {!showMissingLookupRow && totalCount > PAGE_SIZE ? (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!hasPreviousPage || listQuery.isFetching}
                    onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={!hasNextPage || listQuery.isFetching}
                    onClick={() => setOffset((value) => value + PAGE_SIZE)}
                  >
                    Next
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>

      <SimActivateCarrierModal
        open={activateModalOpen}
        iccid={activateModalIccid}
        carrier={activateModalCarrier}
        apiReady={apiReadyForCarrier(activateModalCarrier)}
        pending={singleActivateMutation.isPending}
        onClose={() => {
          if (!singleActivateMutation.isPending) {
            setActivateModalOpen(false);
          }
        }}
        onConfirm={() =>
          singleActivateMutation.mutate({
            iccid: activateModalIccid,
            carrier: activateModalCarrier,
          })
        }
      />

      {addOpen ? (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sim-add-dialog-title"
        >
          <form
            className="w-full max-w-md rounded-lg bg-white shadow-xl"
            onSubmit={(event) => {
              event.preventDefault();
              if (!addCarrier.trim()) {
                showToast({ tone: "error", message: "Select a carrier." });
                return;
              }
              if (!addIccid.trim()) {
                showToast({ tone: "error", message: "ICCID is required." });
                addIccidRef.current?.focus();
                return;
              }
              saveSimMutation.mutate();
            }}
          >
            <div className="border-b border-slate-200 px-4 py-3">
              <h2 id="sim-add-dialog-title" className="text-lg font-semibold text-slate-900">
                Add SIM
              </h2>
            </div>
            <div className="space-y-3 px-4 py-3">
              <div>
                <Label htmlFor="sim-add-carrier">Carrier</Label>
                <HubTouchSelect
                  id="sim-add-carrier"
                  aria-label="Carrier"
                  value={addCarrier}
                  onChange={setAddCarrier}
                  options={[
                    { value: "", label: "Select carrier" },
                    ...carrierOptions.map((carrier) => ({ value: carrier, label: carrier })),
                  ]}
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={addCustomerOwned}
                  onChange={(event) => setAddCustomerOwned(event.target.checked)}
                />
                Customer owned
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={addActivated}
                  onChange={(event) => setAddActivated(event.target.checked)}
                />
                Activated
              </label>
              <div>
                <Label htmlFor="sim-add-iccid">ICCID</Label>
                <Input
                  id="sim-add-iccid"
                  ref={addIccidRef}
                  value={addIccid}
                  autoComplete="off"
                  inputMode="numeric"
                  className="font-mono text-base"
                  onChange={(event) => setAddIccid(event.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={saveSimMutation.isPending}
                onClick={() => setAddOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" size="sm" disabled={!writesAllowed || saveSimMutation.isPending}>
                {saveSimMutation.isPending ? "Saving…" : "Add SIM"}
              </Button>
            </div>
          </form>
        </div>
      ) : null}

      {uploadOpen ? (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sim-upload-dialog-title"
        >
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
            <div className="border-b border-slate-200 px-4 py-3">
              <h2 id="sim-upload-dialog-title" className="text-lg font-semibold text-slate-900">
                Review CSV upload
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                {uploadValid.length} valid row(s), {uploadErrors.length} error row(s).
              </p>
            </div>

            <div className="flex gap-2 border-b border-slate-200 px-4 py-2">
              <Button
                variant={uploadTab === "valid" ? "primary" : "secondary"}
                size="sm"
                onClick={() => setUploadTab("valid")}
              >
                Valid ({uploadValid.length})
              </Button>
              <Button
                variant={uploadTab === "errors" ? "primary" : "secondary"}
                size="sm"
                onClick={() => setUploadTab("errors")}
              >
                Errors ({uploadErrors.length})
              </Button>
            </div>

            <div className="min-h-0 flex-1 overflow-auto p-4">
              {uploadTab === "valid" ? (
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-medium uppercase text-slate-600">
                    <tr>
                      <th className="px-2 py-2">ICCID</th>
                      <th className="px-2 py-2">Carrier</th>
                      <th className="px-2 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {uploadValid.map((row) => (
                      <tr key={`${row.iccid}-${row.carrier}`}>
                        <td className="px-2 py-2 font-mono text-xs">{row.iccid}</td>
                        <td className="px-2 py-2">{row.carrier}</td>
                        <td className="px-2 py-2">{row.isNew ? "New" : "Update"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-medium uppercase text-slate-600">
                    <tr>
                      <th className="px-2 py-2">Row</th>
                      <th className="px-2 py-2">ICCID</th>
                      <th className="px-2 py-2">Carrier</th>
                      <th className="px-2 py-2">Issue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {uploadErrors.map((row, index) => (
                      <tr key={`${row.rowIndex}-${index}`}>
                        <td className="px-2 py-2">{row.rowIndex}</td>
                        <td className="px-2 py-2 font-mono text-xs">{row.iccid || "—"}</td>
                        <td className="px-2 py-2">{row.carrier || "—"}</td>
                        <td className="px-2 py-2 text-rose-700">{row.errorMessage}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="space-y-3 border-t border-slate-200 px-4 py-3">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={uploadCustomerOwned}
                  onChange={(event) => setUploadCustomerOwned(event.target.checked)}
                />
                Customer owned. Already on the customer account. Do not call a carrier API.
              </label>
              {uploadHasApiEligibleRows && !uploadCustomerOwned ? (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={activateWithCarrier}
                    onChange={(event) => setActivateWithCarrier(event.target.checked)}
                  />
                  Also submit Verizon, Vodafone, and AT&T carrier API activation for eligible rows
                </label>
              ) : null}
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={bulkUpsertMutation.isPending}
                  onClick={() => {
                    setUploadOpen(false);
                    setUploadValid([]);
                    setUploadErrors([]);
                  }}
                >
                  Cancel
                </Button>
                <ReadOnlyHoverLayer active={!writesAllowed} hoverHint="Manufacturing write access is required.">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!writesAllowed || uploadValid.length === 0 || bulkUpsertMutation.isPending}
                    isLoading={bulkUpsertMutation.isPending}
                    onClick={() => bulkUpsertMutation.mutate(false)}
                  >
                    Upload as inactive
                  </Button>
                </ReadOnlyHoverLayer>
                <ReadOnlyHoverLayer active={!writesAllowed} hoverHint="Manufacturing write access is required.">
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!writesAllowed || uploadValid.length === 0 || bulkUpsertMutation.isPending}
                    isLoading={bulkUpsertMutation.isPending}
                    onClick={() => bulkUpsertMutation.mutate(true)}
                  >
                    Upload as activated
                  </Button>
                </ReadOnlyHoverLayer>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
